import * as React from "react"

import { NavMain } from "@/components/layout/nav-main"
import { NavSecondary } from "@/components/layout/nav-secondary"
import { NavUser } from "@/components/layout/nav-user"
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/components/ui/sidebar"
import {
  IconCalendarEvent,
  IconCalendarShare,
  IconHistory,
  IconLayoutDashboard,
  IconLifebuoy,
  IconMapPin,
  IconSettings,
  IconShield,
} from "@tabler/icons-react"
import { usePermissions } from "@/hooks/use-permissions"

type AppSidebarProps = React.ComponentProps<typeof Sidebar>

export function AppSidebar({ ...props }: AppSidebarProps) {
  const { hasPermission, hasAnyPermission } = usePermissions()

  const navMain = [
    {
      title: "Tableau de bord",
      url: "/dashboard",
      icon: <IconLayoutDashboard />,
    },
    {
      title: "Réservations",
      url: "/reservations",
      icon: <IconCalendarEvent />,
      isActive: true,
      items: [
        { title: "Toutes les réservations", url: "/reservations" },
        { title: "Mes réservations", url: "/my/reservations" },
        { title: "Agenda", url: "/agenda" },
      ],
    },
    {
      title: "Espaces",
      url: "/rooms",
      icon: <IconMapPin />,
      items: [
        { title: "Salles", url: "/rooms" },
        { title: "Bâtiments", url: "/buildings" },
      ],
    },
  ]

  // Admin nav — only shown if user has any admin permission
  const showAdmin = hasAnyPermission([
    "users.view",
    "settings.view",
    "settings.manage_roles",
    "settings.manage_invitations",
    "devices.view",
    "equipment.view",
  ])

  const adminItems: { title: string; url: string }[] = []
  if (hasPermission("users.view")) adminItems.push({ title: "Utilisateurs", url: "/admin/users" })
  if (hasPermission("settings.manage_roles")) adminItems.push({ title: "Rôles", url: "/admin/roles" })
  if (hasPermission("settings.manage_invitations")) adminItems.push({ title: "Invitations", url: "/admin/invitations" })
  if (hasPermission("devices.view")) adminItems.push({ title: "Appareils", url: "/admin/devices" })
  if (hasPermission("equipment.view")) adminItems.push({ title: "Équipements", url: "/admin/equipment" })

  if (showAdmin && adminItems.length > 0) {
    navMain.push({
      title: "Administration",
      url: adminItems[0].url,
      icon: <IconShield />,
      isActive: false,
      items: adminItems,
    })
  }

  const navSecondary = [
    ...(hasPermission("calendar.manage_own")
      ? [{ title: "Flux iCal", url: "/calendar-sync", icon: <IconCalendarShare /> }]
      : []),
    ...(hasPermission("settings.view_logs")
      ? [{ title: "Journal d'activité", url: "/admin/activity", icon: <IconHistory /> }]
      : []),
    {
      title: "Aide",
      url: "/help",
      icon: <IconLifebuoy />,
    },
    ...(hasPermission("settings.view")
      ? [{ title: "Paramètres", url: "/admin/settings", icon: <IconSettings /> }]
      : []),
  ]

  return (
    <Sidebar variant="inset" {...props}>
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton size="lg" asChild>
              <div className="flex items-center gap-2 cursor-default">
                <div className="flex aspect-square size-8 items-center justify-center rounded-lg bg-sidebar-primary text-sidebar-primary-foreground">
                  <IconCalendarEvent className="size-4" />
                </div>
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-medium">Meeting</span>
                  <span className="truncate text-xs">by Arrow IT</span>
                </div>
              </div>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>
      <SidebarContent>
        <NavMain items={navMain} />
        <NavSecondary items={navSecondary} className="mt-auto" />
      </SidebarContent>
      <SidebarFooter>
        <NavUser />
      </SidebarFooter>
    </Sidebar>
  )
}
